'use client';

import { logger } from '@magicschool/logger';
import {
  channels,
  events,
  handleIntermittentPayloadIssue,
  type MessagePayload,
  type RoomStateChangeMessage,
  type RoomStudentStateChangeMessage,
} from '@magicschool/supabase/realtime';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useStore } from '@/features/store';
import { useAuth } from '@/hooks/useAuth';
import { clientSupabase } from '@/util/supabase';

type StudentRealtimeConnectorClientProps = {
  roomId: string;
};

export const StudentRealtimeConnectorClient: React.FC<StudentRealtimeConnectorClientProps> = ({ roomId }) => {
  const router = useRouter();
  const { handleRoomStateEvent, handleRoomStudentStateEvent } = useStore(
    (s) => ({
      handleRoomStateEvent: s.StudentStoreData.handleRoomStateEvent,
      handleRoomStudentStateEvent: s.StudentStoreData.handleRoomStudentStateEvent,
    }),
    [],
  );
  const { isLTI } = useAuth();

  useEffect(() => {
    if (!roomId) return;

    const roomChannel = clientSupabase.channel(channels.studentRoomChannel(roomId));

    roomChannel
      .on('broadcast', { event: events.roomState }, (event: MessagePayload<RoomStateChangeMessage>) => {
        logger.debug('room', event);
        handleRoomStateEvent(roomId, handleIntermittentPayloadIssue(event.payload).state, router, isLTI);
      })
      .on('broadcast', { event: events.roomStudentState }, (event: MessagePayload<RoomStudentStateChangeMessage>) => {
        logger.debug('roomStudentState', event);
        handleRoomStudentStateEvent(roomId, handleIntermittentPayloadIssue(event.payload), router, isLTI);
      })
      .subscribe((status) => {
        logger.debug('connecting to room', status);
        logger.debug('status changed', status);
        if (status === 'SUBSCRIBED') {
          logger.debug('connected to room');
        }
      });

    const unsubscribe = () => {
      roomChannel.unsubscribe();
    };

    return unsubscribe;
  }, [roomId, handleRoomStateEvent, handleRoomStudentStateEvent, router, isLTI]);

  return null;
};
